import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { Button } from '@common/components/Button';
import debounce from 'lodash/debounce';

import { Table, TABLE_COLUMN_PREDEFINED_ACTION_TYPE } from '@common/components/Table';
import { Flex } from '@common/components/Flex';
import { Search } from '@common/components/Search';
import { Loading } from '@common/components/loading';
import { getAppConfig } from '@common/utils/config';
import { ErrorMessage } from '@common/components/error-message';

import './OrderPatientLinking.styles.scss';

export class OrderPatientLinking extends React.Component {
  appConfig = getAppConfig();
  loadPatientsMetadata = {
    pagination: {}, 
    sorting: {},
    filtering: {}
  };
  cachedColumns = [];
  
  state = {
    loadingData: false,
    selectedPatient: undefined,
    searchKeyword: '',
  };

  componentWillUnmount() {
    this.props.resetPatients();
  }

  componentDidUpdate() {
    const columnsConfigUpdated = this.columns.length > 0 && this.columns.length !== this.cachedColumns.length;

    if (columnsConfigUpdated) {
      this.cachedColumns = this.columns;
    }
  }

  loadPatients = async (params) => {
    const { getPatients } = this.props;
    const requestParams = {
      ...this.loadPatientsMetadata,
      ...params,
    };

    requestParams.pagination.scroll = true;

    this.setState((x) => ({
      ...x,
      loadingData: true,
    }));

    try {
      await getPatients({
        ...requestParams,
        filtering: {
          ...requestParams.filtering,
          searchKeyword: this.state.searchKeyword
        },
      });
    } catch (err) {
      this.props.showMessage({
        header: I18n.t('Operation Canceled'),
        content: <ErrorMessage> {err?.entity || I18n.t('Failed to load data')}</ErrorMessage>,
        show: true,
        confirmText: 'Ok',
        onCustomOk: this.props.closeMessage,
      });

      console.error(err);
    } finally {
      this.setState((x) => ({
        ...x,
        loadingData: false,
      }));
    }
  }

  setLoadPatientsMetadata = (metadata) => {
    this.loadPatientsMetadata = {
      ...this.loadPatientsMetadata,
      ...metadata,
    };
  }

  handlePatientsSearch = ({ searchKeyword }) => {
    this.setState((x) => ({ ...x, searchKeyword }), () => {
      const metadata = {
        ...this.loadPatientsMetadata, 
        pagination: {
          ...this.loadPatientsMetadata.pagination,
          start: 0,
        },
      };
  
      this.setLoadPatientsMetadata(metadata);
      this.loadPatients(metadata);
    });
  }

  handleSubmit = () => {
    const { onLinkPatient, onClose } = this.props;
    
    onLinkPatient(this.state.selectedPatient);
    onClose();
  }

  handleSelectPatient = (selectedPatient) => {
    this.setState((state) => ({ ...state, selectedPatient }));
  }

  get columns() {
    const { patients } = this.props;

    if (!patients.length) {
      return this.cachedColumns;
    }

    const { individualsColumnData } = patients[0];

    return individualsColumnData.map((config) => ({
      sortable: true,
      id: config.fieldName,
      Header: config.fieldName,
      width: config.columnWidth,
      accessor: (patient) => {
        const columnConfig = patient.individualsColumnData.find(({ fieldName }) => fieldName === config.fieldName);

        return columnConfig ? columnConfig.fieldData : '';
      }
    }));
  }

  handleColumnResize = (columns) => {
    columns.forEach(({ id, value }) => {
      const index = this.columns.findIndex(({ Header }) => Header === id);

      this.props.setPatientsColumnsWidth({ columnIndex: index, width: Math.round(value) });
    });
}

  render() {
    const { patients, totalPatientsCount, order, patientFilters, onClose, getFolders, getFields, onAddColumnToPatientsTable } = this.props;

    return (
      <Modal 
        show 
        animation={false}
        className="OrderPatientLinking" 
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="OrderPatientLinking"
        onEscapeKeyDown={onClose}
        onHide={onClose}
      >
        <ModalHeader closeButton onHide={onClose}>
          <ModalTitle id="msg-box-header">
            {I18n.t('Link Order to Patient')}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <Flex className="OrderPatientLinking__order-info">
            <div className="OrderPatientLinking__order-info__name">
              <b>{I18n.t('Name')}: </b>
              {order.patientName}
            </div>

            <div>
              <b>{I18n.t('Order ID')}: </b>
              {order.orderId}
            </div>
          </Flex>

          <Flex alignItems="flex-start">
            <Search 
              withLiveSearch
              className="OrderPatientLinking__search"
              onSearch={this.handlePatientsSearch}
            />
            {this.state.loadingData && <Loading margin="4px 0 0 12px" size={18} />}
          </Flex>

          <Table
            infiniteScroll
            columnActions={[{ type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.filters },  { type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn }]}
            filters={patientFilters}
            moduleName="OrderPatientLinking"
            noDataMessage={I18n.t('There no patients to display')}
            className="OrderPatientLinking__table"
            config={{
              data: patients,
              columns: this.columns,
              onResizedChange: debounce(this.handleColumnResize, 500)
            }}
            itemsCount={totalPatientsCount}
            onLoadData={this.loadPatients}
            onColumnAdd={({ field }) => onAddColumnToPatientsTable(field)}
            setLoadMetadata={this.setLoadPatientsMetadata}
            getFolders={getFolders}
            getFields={getFields}
            onRowClick={this.handleSelectPatient}
          />
        </ModalBody>

        <ModalFooter>
          <Button
            id="btn-ambry-link-order-dlg-cancel"
            key="modalNo"
            onClick={onClose}
          >
            {I18n.t('Cancel')}
          </Button>
          <Button
            id="btn-ambry-link-order-dlg-save"
            key="modalOk"
            type="submit"
            className="btn-primary"
            disabled={!this.state.selectedPatient}
            onClick={this.handleSubmit}
          >
            {I18n.t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}
